import axios, { AxiosInstance } from "axios";
import { useCallback, useMemo, useState, useEffect } from "react";
import { delay } from "../Shared/delay";
import { SingletonRefreshClient } from "./SingletonRefreshClient";
import { clearStoredAuth } from "./authStorage";
import { useTranslation } from "react-i18next";

export function useGateApi<T>(ApiClass: new (url: string, axios: AxiosInstance) => T) {
    // const { i18n } = useTranslation(); // use your hook
    const GetApiAddress = useCallback((): string => {
        return process.env.REACT_APP_GATE_URL!;
    }, []);

    const Unauthorized = useCallback((): T => {
        const axiosApiInstance = axios.create();

        axiosApiInstance.interceptors.request.use(
            async config => {
                const lang = localStorage.getItem('i18nextLng');
                config.headers = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Accept-language': lang ?? 'cs',
                    'TimeZone': Intl.DateTimeFormat().resolvedOptions().timeZone
                }
                return config;
            },
            error => {
                Promise.reject(error)
            });

        const apiInstance = new ApiClass(GetApiAddress(), axiosApiInstance);
        if (!apiInstance) {
            throw new Error("Failed to create Unauthorized API instance");
        }
        return apiInstance;
    }, [ApiClass, GetApiAddress]);

    const AuthorizedForTenants = useCallback((): T => {
        const axiosApiInstance = axios.create();
        const token = localStorage.getItem('temp_tenant_token');

        axiosApiInstance.interceptors.request.use(
            async config => {
                const lang = localStorage.getItem('i18nextLng');
                config.headers = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Accept-language': lang ?? 'cs',
                    'TimeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
                    'Authorization': 'Bearer ' + token
                }
                return config;
            },
            error => {
                Promise.reject(error)
            });

        const apiInstance = new ApiClass(GetApiAddress(), axiosApiInstance);
        if (!apiInstance) {
            throw new Error("Failed to create Unauthorized API instance");
        }
        return apiInstance;
    }, [ApiClass, GetApiAddress]);

    // const [token, setToken] = useState(localStorage.getItem('token'));

    // useEffect(() => {
    //     const handleStorageChange = () => {
    //         setToken(localStorage.getItem('token'));
    //     };

    //     window.addEventListener('storage', handleStorageChange);
    //     return () => {
    //         window.removeEventListener('storage', handleStorageChange);
    //     };
    // }, []);

    const Authorized = useCallback((): T | null => {
        const axiosApiInstance = axios.create();
        // console.log('token', token);
        // const token = ;
        // if (!token) return null;

        axiosApiInstance.interceptors.request.use(
            async config => {
                const lang = localStorage.getItem('i18nextLng');
                config.headers = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'TimeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
                    'Authorization': 'Bearer ' + localStorage.getItem('token'),
                    'Accept-language': lang ?? 'cs'
                }
                return config;
            },
            error => {
                Promise.reject(error)
            });
        axiosApiInstance.interceptors.response.use((response) => {
            return response
        }, async function (error) {
            const originalRequest = error.config;
            // if (error.response.status === 401) {
            //     console.log('401.1', window.location.href);
            //     var items = window.location.href.split("/");
            //     window.location.href = window.location.origin + '/' + items[3] + '/' + items[4];
            // }
            if (error.response.status === 401 && !originalRequest._retry) {
                console.log('tady');
                console.log(error);
                // console.log(response);
                // The token this request was actually sent with. A login finishing in parallel
                // (a tool login coming back through /auth-callback) can store a brand new
                // session while the request is in flight - the 401 then belongs to the old
                // session and must neither refresh nor clear the new one, just be retried.
                const requestAuthHeader = originalRequest?.headers?.Authorization ?? originalRequest?.headers?.authorization;
                const requestToken = typeof requestAuthHeader === 'string'
                    ? requestAuthHeader.replace('Bearer ', '')
                    : null;
                const storedToken = localStorage.getItem('token');
                if (requestToken && storedToken && storedToken !== requestToken) {
                    originalRequest._retry = true;
                    return axiosApiInstance(originalRequest);
                }
                try {
                    if (error.response.headers['is-token-expired'] === 'true') {
                        if (!SingletonRefreshClient.isRefreshing) {
                            SingletonRefreshClient.setRefresh();
                            originalRequest._retry = true;
                            // Only the session we really failed to refresh may be dropped, so
                            // remember which one that is.
                            const refreshedFrom = localStorage.getItem('token');
                            try {
                                await SingletonRefreshClient.refreshToken();
                            }
                            catch (e) {

                                console.log(e);
                                // Drop only the session that failed to refresh. This used to be
                                // localStorage.clear(), which also wiped the state of a login in
                                // progress ('ms-redirect', 'ms-state', 'tool-login',
                                // 'redirect-server') and dropped tool logins on /portal instead
                                // of returning them to the tool.
                                if (localStorage.getItem('token') === refreshedFrom) {
                                    clearStoredAuth();
                                }
                                SingletonRefreshClient.completeRefresh();
                                return Promise.reject(error);

                            }
                            axios.defaults.headers.common['Authorization'] = 'Bearer ' + localStorage.getItem('token');
                            SingletonRefreshClient.completeRefresh();
                        }
                        else await delay(400);

                        return axiosApiInstance(originalRequest);
                    }
                    else throw new Error('unauthorized but refresh token not expires');
                }
                catch (e) {
                    console.log('error refresh', e);
                    // throw new Error('unable to refresh token');
                }
            }
            return Promise.reject(error);
        });

        const apiInstance = new ApiClass(GetApiAddress(), axiosApiInstance);
        if (!apiInstance) {
            throw new Error("Failed to create Unauthorized API instance");
        }
        return apiInstance;
    }, [ApiClass, GetApiAddress]);
    const [resetAuthorized, setResetAuthorized] = useState(false);
    const resetAuthorizedApi = useCallback(() => {
        setResetAuthorized(!resetAuthorized);
    }, [setResetAuthorized, resetAuthorized]);
    const authorizedApi = useMemo(() => {

        const apiInstance = Authorized();

        return apiInstance;
    }, [Authorized]);

    const unauthorizedApi = useMemo(() => Unauthorized(), [Unauthorized]);
    const authorizedApiForTenants = useMemo(() => AuthorizedForTenants(), [AuthorizedForTenants]);

    return { unauthorizedApi, Unauthorized, authorizedApiForTenants, AuthorizedForTenants, authorizedApi, resetAuthorizedApi, Authorized };
}